import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

// Mismo corte que useMediaQuery(): debajo de este ancho la app es "celular".
const MOBILE_BREAKPOINT = '768px';
const FORM_STACK_BREAKPOINT = '600px';

const GlobalStyle = createGlobalStyle`
  body {
    font-family: ${theme.typography.fontFamily};
    background-color: ${theme.colors.background};
    color: ${theme.colors.secondary};
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }

  /* App instalada en celulares con muesca (viewport-fit=cover): el contenido no
     queda debajo de la barra de estado ni del indicador de inicio. En el
     navegador común los env() valen 0 y no cambia nada. */
  #root {
    padding-top: env(safe-area-inset-top);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
  }

  /* Franja Azul Axon detrás de la barra de estado del celular, para que la hora
     y la batería se lean en todas las pantallas. */
  body::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: env(safe-area-inset-top);
    background-color: ${theme.colors.primary};
    z-index: 3050;
    pointer-events: none;
  }

  /* Pantallas táctiles */
  @media (hover: none) and (pointer: coarse) {
    html {
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }

    body {
      -webkit-tap-highlight-color: rgba(26, 40, 62, 0.12);
    }

    /* iPhone hace zoom automático al tocar un campo con letra menor a 16px y
       después no vuelve a su tamaño: se fuerza 16px solo en táctiles. */
    input, select, textarea {
      font-size: 16px !important;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.typography.titleFamily};
    color: ${theme.colors.primary};
  }

  /* ------------------------------------------------------------------------
     Celular. Casi todos los componentes usan estilos inline, así que estas
     clases utilitarias llevan !important para poder pisarlos. Todo queda
     detrás del ancho de pantalla: en escritorio no cambia nada.
     ------------------------------------------------------------------------ */
  /* Grillas de formulario de 2, 3 o 4 columnas: una sola en el celular (en
     una tablet entran las columnas de escritorio). */
  @media (max-width: ${FORM_STACK_BREAKPOINT}) {
    .axon-form-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    .axon-form-grid > * {
      grid-column: 1 / -1 !important;
      grid-row: auto !important;
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {

    /* Filas de sub-pestañas o chips: se desplazan de costado en vez de
       ensanchar toda la página. */
    .axon-scroll-x {
      display: flex;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      max-width: 100%;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .axon-scroll-x::-webkit-scrollbar {
      display: none;
    }
    .axon-scroll-x > * {
      flex-shrink: 0 !important;
      white-space: nowrap !important;
    }

    /* Barras de título + acciones que en escritorio van en una sola línea. */
    .axon-wrap {
      flex-wrap: wrap !important;
    }
    .axon-wrap-stretch > * {
      flex: 1 1 auto;
    }
    /* Grupo de botones apilados a lo ancho (selector de rol). */
    .axon-stack {
      flex-direction: column !important;
    }
    .axon-stack > * {
      flex: 0 0 auto !important;
      width: 100%;
    }
    /* Buscadores y controles con ancho mínimo fijo: a lo ancho de la pantalla. */
    .axon-fill {
      flex: 1 1 100% !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
    }

    /* Indicadores y filtros: de a dos por fila. */
    .axon-grid-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 0.5rem !important;
    }
    .axon-kpi-card {
      padding: 0.75rem !important;
      min-width: 0;
    }
    .axon-kpi-value {
      font-size: 1.05rem !important;
      overflow-wrap: anywhere;
    }

    /* Modales armados a mano (avisos, recordatorios): nunca más altos que la
       pantalla, y los botones bajan de línea en vez de salirse. */
    .axon-modal-sheet {
      width: calc(100% - 1rem) !important;
      max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 1rem) !important;
      overflow-y: auto !important;
      padding: 1.25rem !important;
    }
    .axon-modal-actions {
      flex-wrap: wrap !important;
    }
    .axon-modal-actions > * {
      flex: 1 1 auto;
      min-width: 0 !important;
    }

    /* Aviso flotante a lo ancho, por encima del indicador de inicio. */
    .axon-toast {
      left: 0.75rem !important;
      right: 0.75rem !important;
      bottom: calc(env(safe-area-inset-bottom) + 0.75rem) !important;
    }
    .axon-toast button {
      min-width: 44px;
      min-height: 44px;
    }

    /* La barra de scroll superior de las tablas anchas no aplica a tarjetas. */
    .axon-top-scrollbar {
      display: none !important;
    }

    /* --- Tablas como tarjetas (ver utils/stackedTable.js) ------------------ */
    /* Solo el scroll horizontal: un contenedor con scroll vertical (dentro
       de un modal, por ejemplo) lo conserva. */
    [data-stack-wrap] {
      overflow-x: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }
    table.axon-stacked {
      display: block;
      width: 100% !important;
      min-width: 0 !important;
    }
    table.axon-stacked > thead {
      display: none;
    }
    table.axon-stacked > tbody,
    table.axon-stacked > tfoot {
      display: block;
    }
    /* Cada fila es una tarjeta con los campos de a dos: etiqueta arriba y
       valor abajo. Título, botones y "Ver detalle" ocupan el ancho completo. */
    table.axon-stacked > tbody > tr,
    table.axon-stacked > tfoot > tr {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 0.75rem;
      margin: 0 0 0.75rem;
      padding: 0.4rem 0.9rem;
      border: 1px solid ${theme.colors.border};
      border-radius: 10px;
      box-shadow: 0 1px 3px rgba(13, 26, 41, 0.08);
    }
    /* Fondo por defecto sin especificidad: el color de estado de la fila
       (inline o por clase) siempre gana. */
    :where(table.axon-stacked > tbody > tr, table.axon-stacked > tfoot > tr) {
      background-color: ${theme.colors.surface};
    }
    table.axon-stacked > tbody > tr > td,
    table.axon-stacked > tfoot > tr > td {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 0.1rem;
      order: 20;
      width: auto !important;
      min-width: 0 !important;
      max-width: none !important;
      height: auto !important;
      padding: 0.4rem 0 !important;
      border: none !important;
      border-bottom: 1px solid rgba(13, 26, 41, 0.06) !important;
      text-align: left !important;
      white-space: normal !important;
      overflow-wrap: anywhere;
    }
    table.axon-stacked > tbody > tr > td::before,
    table.axon-stacked > tfoot > tr > td::before {
      content: attr(data-label);
      font-size: 0.68rem;
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: ${theme.colors.textSecondary};
      overflow-wrap: normal;
    }
    table.axon-stacked td[data-stack-title],
    table.axon-stacked > tbody > tr[data-stack-section] > td,
    table.axon-stacked tr[data-stack-collapsible]::after {
      grid-column: 1 / -1;
    }
    table.axon-stacked td[data-label='']::before,
    table.axon-stacked td[data-stack-title]::before,
    table.axon-stacked td[data-stack-actions]::before {
      content: none;
    }
    table.axon-stacked td[data-stack-empty] {
      display: none !important;
    }
    table.axon-stacked td[data-stack-title] {
      order: -1;
      font-size: 1rem;
      font-weight: 700;
      color: ${theme.colors.secondary};
    }
    /* Botones a la derecha, en el primer hueco libre después de los campos. */
    table.axon-stacked td[data-stack-actions] {
      order: 90;
      grid-column: -2 / -1;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      align-self: end;
      flex-wrap: wrap;
      gap: 0.5rem;
      border-bottom: none !important;
    }
    table.axon-stacked td[data-stack-actions] button {
      min-width: 44px;
      min-height: 44px;
      margin: 0 !important;
    }
    /* En la tarjeta, un botón deshabilitado es ruido: se oculta. */
    table.axon-stacked td[data-stack-actions] button:disabled {
      display: none;
    }
    table.axon-stacked td select {
      width: 100% !important;
      min-height: 40px;
    }
    /* Botones dentro de la tarjeta ("Tomar", "Parciales", "Cargada"). */
    table.axon-stacked td button {
      min-height: 40px;
    }
    /* Checkboxes de "Cargada", "Caja" o selección: tocables con el dedo. */
    table.axon-stacked td input[type='checkbox'] {
      width: 22px !important;
      height: 22px !important;
      margin: 0.25rem 0 !important;
    }

    /* Títulos de pestaña más chicos para que no ocupen dos líneas. */
    .axon-page-title {
      font-size: 1.5rem !important;
    }

    /* Acción principal de la pestaña ("+ Agregar ...") flotando abajo a la
       derecha: queda a mano aunque se haya bajado por la planilla. */
    .axon-fab {
      position: fixed !important;
      right: calc(env(safe-area-inset-right) + 1rem);
      bottom: calc(env(safe-area-inset-bottom) + 1rem);
      z-index: 1200;
      min-height: 52px;
      padding: 0 1.25rem !important;
      border-radius: 999px !important;
      box-shadow: 0 7px 18px rgba(13, 26, 41, 0.35);
    }
    ${Array.from({ length: 20 }, (_, i) => `table.axon-stacked td[data-stack-order='${i}'] { order: ${i}; }`).join('\n    ')}

    /* Filas de sección (una sola celda con colSpan): título de grupo. */
    table.axon-stacked > tbody > tr[data-stack-section] {
      padding: 0.25rem 0;
      background-color: transparent;
      border: none;
      box-shadow: none;
    }
    table.axon-stacked > tbody > tr[data-stack-section] > td {
      display: block;
      text-align: left !important;
      border-bottom: none !important;
      border-radius: 8px;
      padding: 0.6rem 0.75rem !important;
    }

    /* Tarjetas colapsadas: solo las columnas principales; el resto con
       "Ver detalle". */
    table.axon-stacked tr[data-stack-collapsible]:not([data-stack-expanded]) > td[data-stack-secondary] {
      display: none !important;
    }
    table.axon-stacked tr[data-stack-collapsible]::after {
      content: 'Ver detalle ▾';
      order: 100;
      padding: 0.6rem 0 0.4rem;
      text-align: center;
      font-size: 0.8rem;
      font-weight: 600;
      color: ${theme.colors.primary};
      cursor: pointer;
    }
    table.axon-stacked tr[data-stack-collapsible][data-stack-expanded]::after {
      content: 'Ocultar detalle ▴';
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default GlobalStyle;